import React, { useEffect, useState } from 'react';
import { healthAPI } from '../api';
import { LogEntry } from '../types';
import { RefreshCcw, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import './OperationalDashboard.css';

export const OperationalDashboard: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await healthAPI.getOperationalData();
            setLogs(data.logs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const getLogIcon = (message: string) => {
        if (message.includes('ERROR') || message.includes('Exception')) return <AlertTriangle className="error-icon" />;
        if (message.includes('WARNING')) return <AlertCircle className="warning-icon" />;
        return <Info className="info-icon" />;
    };

    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    const filteredLogs = showErrorsOnly
        ? logs.filter(log => log.message.includes('ERROR') || log.message.includes('Exception') || log.message.includes('AccessDenied'))
        : logs;

    return (
        <div className="operational-dashboard">
            <div className="dashboard-header">
                <h2>Operational Dashboard</h2>
                <div className="header-controls">
                    <button
                        className={`filter-button ${showErrorsOnly ? 'active' : ''}`}
                        onClick={() => setShowErrorsOnly(!showErrorsOnly)}
                    >
                        <AlertTriangle size={18} />
                        {showErrorsOnly ? 'Showing Errors Only' : 'Show Errors Only'}
                    </button>
                    <button onClick={fetchLogs} disabled={loading} className="refresh-button">
                        <RefreshCcw size={18} className={loading ? 'spinning' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="logs-container">
                <h3>Recent System Logs (Errors & Warnings)</h3>
                {loading && logs.length === 0 ? (
                    <div className="loading-state">Loading logs...</div>
                ) : logs.length === 0 ? (
                    <div className="empty-state">No relevant logs found in the last 7 days.</div>
                ) : filteredLogs.length === 0 ? (
                    <div className="empty-state">No error logs found.</div>
                ) : (
                    <div className="logs-list">
                        {filteredLogs.map((log, index) => (
                            <div key={index} className="log-entry">
                                <div className="log-meta">
                                    <span className="log-time">{formatTime(log.timestamp)}</span>
                                    <span className="log-group">{log.logGroup.split('/').pop()}</span>
                                </div>
                                <div className="log-content">
                                    {getLogIcon(log.message)}
                                    <pre className="log-message">{log.message}</pre>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
